import { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import megamenuData from '../../../megamenu.json';

// Exact tool path → bilingual label, derived from the megamenu catalog so every
// /tools/* page gets a title without listing each one here.
const TOOL_TITLES = {};
for (const category of megamenuData.categories) {
  for (const tool of category.tools) {
    TOOL_TITLES[tool.path] = { en: tool.name, bn: tool.nameBn };
  }
}

// Ordered: first match wins, so specific patterns must come before splats.
const ROUTE_TITLES = [
  // Public / standalone
  ['/login', 'navigation.login'],
  ['/register/*', 'navigation.register'],
  ['/forgot-password', 'pageTitles.forgotPassword'],
  ['/mobile-upload/:token', 'pageTitles.mobileUpload'],
  ['/u/:slug', 'pageTitles.photoCollect'],
  ['/signature-mobile/:sessionSlug', 'pageTitles.signaturePad'],
  ['/payment/success', 'pageTitles.paymentSuccess'],
  ['/payment/failed', 'pageTitles.paymentFailed'],

  // Main modules
  ['/', 'navigation.dashboard'],
  ['/generate/:templateSetId', 'navigation.dashboard'],
  ['/template-sets/*', 'navigation.documentSets'],
  ['/documents/*', 'navigation.myDocuments'],
  ['/generate-image', 'navigation.generateImageAI'],
  ['/generate-image-manual', 'navigation.generateImageManual'],
  ['/studio-editor', 'navigation.studioEditor'],
  ['/bulk-photo-edit/*', 'navigation.bulkPhotoEdit'],
  ['/ai-documents/*', 'navigation.aiDocuments'],
  ['/ai-templates/*', 'navigation.aiTemplates'],
  ['/worldcup-jersey/*', 'navigation.aiTemplates'],
  ['/questions-create/*', 'navigation.questionPapers'],
  ['/print-media/*', 'navigation.printMedia'],
  ['/jobs/*', 'navigation.jobsMenu'],
  ['/dorkari-links', 'navigation.sorkariLinks'],
  ['/income', 'navigation.incomeExpense'],
  ['/income/customers/:id', 'pageTitles.customerDetails'],
  ['/officespaces/*', 'navigation.officeSpaces'],
  ['/profile', 'navigation.profile'],
  ['/settings', 'settings.title'],
  ['/onboard', 'pageTitles.onboarding'],

  // Tools pages not covered by the megamenu catalog
  ['/tools', 'navigation.sohozTools'],
  ['/tools/result/education-board-result', 'educationBoardResult.title'],
  ['/tools/result/national-university-result', 'nationalUniversityResult.title'],
  ['/tools/form-auto-fillup/*', 'navigation.formAutoFillup'],
  ['/tools/*', 'navigation.sohozTools'],

  // Admin
  ['/admin', 'pageTitles.adminDashboard'],
  ['/admin/template-sets/*', 'navigation.templateSets'],
  ['/admin/print-templates/*', 'navigation.printTemplateTemplates'],
  ['/admin/print-template-categories', 'navigation.printTemplateCategories'],
  ['/admin/print-feedback', 'navigation.printFeedback'],
  ['/admin/tags', 'navigation.tags'],
  ['/admin/categories', 'navigation.categories'],
  ['/admin/generate-image', 'navigation.generateImageAI'],
  ['/admin/generate-image-manual', 'navigation.generateImageManual'],
  ['/admin/ai-templates/*', 'navigation.aiTemplates'],
  ['/admin/ai-template-categories', 'navigation.aiTemplateCategories'],
  ['/admin/jobs/*', 'navigation.adminJobs'],
  ['/admin/profile', 'navigation.profile'],
  ['/admin/settings', 'settings.title'],
  ['/admin/doc-editor/*', 'pageTitles.docEditor'],
  ['/admin/lexical/*', 'pageTitles.docEditor'],
  ['/admin/lexical-editor/*', 'pageTitles.docEditor'],
  ['/admin/doc-print-preview/*', 'pageTitles.printPreview'],
];

export default function DynamicPageTitle() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const isBn = (i18n.language || '').startsWith('bn');
    const brand = t('pageTitles.brand');

    let pageName = null;
    const tool = TOOL_TITLES[pathname];
    if (tool) {
      pageName = isBn ? tool.bn : tool.en;
    } else {
      const hit = ROUTE_TITLES.find(([pattern]) => matchPath(pattern, pathname));
      if (hit) pageName = t(hit[1]);
    }

    document.title = pageName ? `${brand} - ${pageName}` : brand;
  }, [pathname, i18n.language, t]);

  return null;
}
